import React, { useMemo, createContext, useEffect } from 'react';
import Config from 'config';
import { Box, Grid } from '@apia/theme';
import { useLatest, useMount } from 'ahooks';
import { AccordionElement, Modal } from '../../../common';
import IProperty from '../../../../JSApi/constants/IProperty';
import {
  TApiaFieldPropertiesObj,
  TApiaForm,
  TApiaFormPropertiesObj,
  TFrmParent,
} from '../../../../types';
import SimpleButton from '../../../common/buttons/SimpleButton';
import ApiaApi, { makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import { useVerifySignModal } from '../digitalSign/VerifySignModal';
import { ApiaComponentsRenderer } from './ApiaComponentsRenderer';
import ApiaEditGridForm from './ApiaEditGridForm';
import { checkAllFieldsAreMounted, getFormIDs } from '../utils/executionUtil';
import formsEventsController from '../formsEvents/formsEventsController';
import useContext from '../../../../hooks/useContext';
import propsStore from '../../../../store/propsStore/propsStore';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import ApiaDebugInfo from '../common/ApiaDebugInfo';
import MarkToSignButton from './MarkToSignButton';
import makeImperativeComponent from '../../../../classes/imperative/makeImperativeComponent';
import useModal from '../../../../hooks/useModal';

export interface IApiaFormComponent {
  formData: TApiaForm;
  formId: string;
}

export interface IApiaFormContext {
  formData: TApiaForm;
  formId: string;
  frmParent: TFrmParent;
  isFormReadOnly: boolean;
  isFormClosed: boolean;
  parsedFormId: string;
}

export const ApiaFormContext = createContext<IApiaFormContext | undefined>(
  undefined,
);
ApiaFormContext.displayName = 'ApiaFormContext';

export function useApiaFormContext() {
  return useContext(ApiaFormContext);
}

const [apiaFormMethods, , ApiaForm] =
  makeImperativeComponent<IApiaFormComponent>()({
    initialState: {
      asModal: false,
    },
    methods: {
      showAsModal(setState, asModal: boolean) {
        setState({ asModal });
      },
    },
    Component: ({ asModal, formData, formId }) => {
      const { formName, formTitle, cols } = formData;
      const isFormClosed = formData.prpFrmClosed;
      const isFormReadOnly =
        formData.readOnly ||
        formData.readonly ||
        !!window.location.search.match(/isReadonly=true/);
      const frmParent = formId.substring(0, 1) as TFrmParent;
      const parsedFormId = formId.split('_').pop() || formId;

      const contextValue: IApiaFormContext = useMemo(
        () => ({
          formData,
          formId,
          frmParent,
          isFormReadOnly,
          isFormClosed,
          parsedFormId,
        }),
        [
          formData,
          formId,
          frmParent,
          isFormClosed,
          isFormReadOnly,
          parsedFormId,
        ],
      );

      // sign hook
      const setVerifySignURL = useVerifySignModal();

      const formIDsList = useMemo(
        () => getFormIDs(formData.field, formId),
        [formData.field, formId],
      );

      const areAllMounted = React.useRef(false);

      // set form props hook
      useMount(() => {
        propsStore.updateField(formId, {
          formData,
          formId,
          frmParent,
          isFormReadOnly,
          isFormClosed,
          parsedFormId,
          [IProperty.PROPERTY_FORM_CLOSED]: isFormClosed,
          [IProperty.PROPERTY_FORM_READONLY]: isFormReadOnly,
          ...formData.properties,
        });

        const mountedComponentsList: string[] = [];

        const unregister = propsStore.subscribe('any', (change) => {
          if (
            !change.fieldId ||
            !change.fieldId.startsWith(formId) ||
            !change.hasFinishedLoading ||
            areAllMounted.current ||
            mountedComponentsList.includes(change.fieldId)
          ) {
            return;
          }
          mountedComponentsList.push(change.fieldId);
          areAllMounted.current = checkAllFieldsAreMounted(
            mountedComponentsList,
            formIDsList,
          );
          if (areAllMounted.current) {
            unregister();
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                formsEventsController.markAsLoaded(formId);
              });
            });
          }
        });
      });
      const frmProps = usePropsSelector<
        TApiaFormPropertiesObj,
        TApiaFormPropertiesObj
      >(formId);

      const handleAccordionClick = () => {
        if (!window.location.href.match(/viewItem=true/)) {
          void ApiaApi.post(
            makeApiaUrl({
              ajaxUrl: 'apia.execution.FormAction.run',
              action: 'closeForm',
              frmId: formData.id,
              frmParent,
              collapse: !frmProps[IProperty.PROPERTY_FORM_CLOSED],
            }),
          );
          if (frmProps[IProperty.PROPERTY_FORM_CLOSED])
            propsStore.updateField(formId, {
              [IProperty.PROPERTY_FORM_CLOSED]: false,
            });
          else
            propsStore.updateField(formId, {
              [IProperty.PROPERTY_FORM_CLOSED]: true,
            });
        }
      };

      const { show, ...props } = useModal({
        onExited() {
          apiaFormMethods.showAsModal(formId, false);
        },
      });
      const showRef = useLatest(show);
      useEffect(() => {
        if (asModal) showRef.current();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [asModal]);

      const content = (
        <Box
          role="form"
          id={formId}
          className={`form ${formName}`}
          aria-label={formTitle}
          data-form-name={formName}
          data-form-title={formTitle}
          data-form-readonly={frmProps[IProperty.PROPERTY_FORM_READONLY]}
          data-form-collapsed={frmProps[IProperty.PROPERTY_FORM_CLOSED]}
          data-form-type={formId.slice(0, 1)}
          {...window.getVariant('layout.execution.form')}
        >
          {formData.signableForm && (
            <Box
              {...window.getVariant(
                'layout.execution.components.digitalSign.buttons',
              )}
            >
              <MarkToSignButton
                id={`${frmParent}_${formData.id}`}
                frmParent={frmParent}
                formId={formData.id}
                isDefaultMarkedToSign={formData.markedToSign}
                requiredSignableForm={formData.requiredSignableForm}
              />
              <SimpleButton
                onClick={() => {
                  setVerifySignURL(
                    makeApiaUrl({
                      ajaxUrl: '/apia.execution.FormAction.run',
                      action: 'viewSigns',
                      frmId: formData.id,
                      frmParent,
                      react: true,
                    }),
                  );
                }}
              >
                {window.BTN_VERIFY_FRM_SIGN}
              </SimpleButton>
            </Box>
          )}
          <Grid
            columns={cols}
            className={`grid--${cols} form__grid`}
            {...window.getVariant(
              !frmProps[IProperty.PROPERTY_FORM_READONLY]
                ? 'grids.execution.form'
                : 'grids.execution.formReadOnly',
            )}
          >
            <ApiaComponentsRenderer formId={formId} formData={formData} />
          </Grid>
        </Box>
      );

      if (frmProps && !Config.IS_EDIT_GRID_MODAL) {
        return (
          <ApiaFormContext.Provider value={contextValue}>
            <ApiaDebugInfo data={formData} id={formId} />
            {asModal ? (
              <Modal size="flex" title={formTitle} {...props}>
                {content}
              </Modal>
            ) : (
              <AccordionElement
                key={`accordion_${formId}`}
                id={`accordion_${formId}`}
                title={formTitle}
                onClick={handleAccordionClick}
                initiallyExpanded={isFormClosed === false}
                frmHidden={frmProps[IProperty.PROPERTY_FORM_HIDDEN]}
                expanded={frmProps[IProperty.PROPERTY_FORM_CLOSED] !== true}
                allowToggle={!window.location.href.match(/viewItem=true/)}
              >
                {content}
              </AccordionElement>
            )}
          </ApiaFormContext.Provider>
        );
      }
      if (frmProps && Config.IS_EDIT_GRID_MODAL) {
        // Editable Grid Form
        return (
          <ApiaEditGridForm
            formId={formId}
            formName={formName}
            formTitle={formTitle}
            frmProps={frmProps as TApiaFieldPropertiesObj}
            formData={formData}
          >
            <ApiaFormContext.Provider value={contextValue}>
              <ApiaComponentsRenderer formId={formId} formData={formData} />
            </ApiaFormContext.Provider>
          </ApiaEditGridForm>
        );
      }
      return null;
    },
  });

export { apiaFormMethods };
export default ApiaForm;
